import { useMemo } from "react";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import CampaignIcon from "@mui/icons-material/Campaign";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import EventIcon from "@mui/icons-material/Event";
import CelebrationIcon from "@mui/icons-material/Celebration";
import CakeIcon from "@mui/icons-material/Cake";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import GroupsIcon from "@mui/icons-material/Groups";
import StarIcon from "@mui/icons-material/Star";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { useTenant } from "../contexts/TenantContext";

/**
 * The icons a blog post may put in front of its menu entry, by the name the tenant's
 * `menuIcon` uses. A fixed list rather than "any MUI icon": every icon is a module of its
 * own, and resolving arbitrary names at runtime would either bundle all two thousand of
 * them or split each into a chunk. Adding one is a line here. An unknown or missing name
 * gets `article`.
 */
export const BLOG_POST_ICONS = {
  article: ArticleOutlinedIcon,
  games: SportsEsportsIcon,
  vr: ViewInArIcon,
  car: DirectionsCarIcon,
  news: NewReleasesIcon,
  campaign: CampaignIcon,
  offer: LocalOfferIcon,
  gift: CardGiftcardIcon,
  event: EventIcon,
  celebration: CelebrationIcon,
  cake: CakeIcon,
  trophy: EmojiEventsIcon,
  groups: GroupsIcon,
  star: StarIcon,
  music: MusicNoteIcon,
  photo: PhotoCameraIcon,
};

const isText = (value) => typeof value === "string" && value.trim().length > 0;

// "Játékok" -> "jatekok". Accents are stripped rather than percent-encoded: the result is an
// element id and the fragment of a link people copy around, and it has to survive both.
const slugify = (value) => (isText(value) ? value : "")
  .normalize("NFD")
  .replace(/[̀-ͯ]/g, "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");

const textBlock = (type, { text }) => (isText(text) ? { type, text } : null);

const BLOCKS = {
  paragraph: (block) => textBlock("paragraph", block),
  heading: (block) => textBlock("heading", block),
  list: ({ items }) => {
    const texts = Array.isArray(items) ? items.filter(isText) : [];
    return texts.length > 0 ? { type: "list", items: texts } : null;
  },
  image: ({ src, alt, caption }) => (isText(src)
    ? { type: "image", src, alt: isText(alt) ? alt : "", caption: isText(caption) ? caption : null }
    : null),
};

// A block of a type this build does not know is dropped, not rendered as nothing in a box:
// a config written for a newer frontend must not break the page of an older one.
const normalizeBlock = (block) => (block && BLOCKS[block.type] ? BLOCKS[block.type](block) : null);

/**
 * `displayConfig.homePage.blogPosts` as the page and the header use it. The config is
 * hand-written JSON the template validator deliberately does not look inside, so nothing
 * here trusts its shape: a post without a title or without a single renderable block is
 * left out entirely — from the page and from the menu both, so no menu entry ever points
 * at a section that is not there.
 *
 * Each post gets an anchor: its `slug`, else its title slugified, prefixed with `post-` so
 * it cannot collide with the page's own `#about`, `#gallery` and the rest.
 */
export const normalizeBlogPosts = (rawPosts) => {
  if (!Array.isArray(rawPosts)) return [];

  const usedAnchorIds = new Set();

  return rawPosts
    .filter((post) => post && isText(post.title))
    .map((post, index) => ({
      post,
      blocks: (Array.isArray(post.blocks) ? post.blocks : []).map(normalizeBlock).filter(Boolean),
      base: slugify(post.slug) || slugify(post.title) || String(index + 1),
    }))
    .filter(({ blocks }) => blocks.length > 0)
    .map(({ post, blocks, base }) => {
      // Two posts that slug the same would share an anchor, and the second one's menu entry
      // would scroll to the first.
      let anchorId = `post-${base}`;
      for (let suffix = 2; usedAnchorIds.has(anchorId); suffix++) {
        anchorId = `post-${base}-${suffix}`;
      }
      usedAnchorIds.add(anchorId);

      return {
        anchorId,
        route: `/#${anchorId}`,
        title: post.title,
        menuLabel: isText(post.menuLabel) ? post.menuLabel : post.title,
        // The menu is the only place a post's icon appears.
        MenuIcon: BLOG_POST_ICONS[post.menuIcon] || BLOG_POST_ICONS.article,
        isInMenu: post.isInMenu === true,
        blocks,
      };
    });
};

const useBlogPosts = () => {
  const { homePage } = useTenant();
  const rawPosts = homePage?.blogPosts;

  return useMemo(() => normalizeBlogPosts(rawPosts), [rawPosts]);
};

export default useBlogPosts;
